import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Box, Dialog, DialogActions, DialogContent } from '@mui/material';
import {
  GRID_CHECKBOX_SELECTION_FIELD,
  GridCellParams,
  GridRowHeightParams,
  GridRowParams,
  MuiEvent,
} from '@mui/x-data-grid-premium';

import { DEFAULT_COLUMN_BUFFER_PX, ROW_OVERSCAN_PX } from './GeneratikTable.constants';
import {
  CONTEXT_MENU_ICON_SLOTS,
  DetailPanelCollapseIcon,
  DetailPanelExpandIcon,
} from './GeneratikTable.icons';
import { StyledGeneratikDataGrid, TableContainer } from './GeneratikTable.styles';
import GeneratikTableColumnMenu from './GeneratikTableColumnMenu';
import { useGeneratikTableContext } from './GeneratikTableContext';
import {
  buildDataGridSx,
  dialogActionsSx,
  dialogContentSx,
  getDialogSx,
  predefinedFiltersWrapperSx,
  tableContainerSx,
} from './GeneratikTableDialogDisplay.styles';
import GeneratikTableFilterPanel from './GeneratikTableFilterPanel';
import GeneratikTableFooter from './GeneratikTableFooter';
import GeneratikTableHeader from './GeneratikTableHeader';
import GeneratikTableHeadersLoadingPlaceholder, {
  shouldShowHeadersLoadingPlaceholder,
} from './GeneratikTableHeadersLoadingPlaceholder';
import GeneratikTableListView from './GeneratikTableListView';
import GeneratikTableToolbar from './GeneratikTableToolbar';
import SortPriorityBadge from './SortPriorityBadge';
import useDataGridScrollbars from './hooks/useDataGridScrollbars';
import useDataGridVerticalScrollbar from './hooks/useDataGridVerticalScrollbar';
import useSelectionCheckboxColumns from './hooks/useSelectionCheckboxColumns';

const GeneratikTableDialogDisplay = () => {
  const { t } = useTranslation();
  const ctx = useGeneratikTableContext();
  const tableContainerRef = useRef<HTMLDivElement | null>(null);
  const [isTableContainerMounted, setIsTableContainerMounted] = useState(false);
  const setTableContainer = useCallback((element: HTMLDivElement | null) => {
    tableContainerRef.current = element;
    setIsTableContainerMounted(!!element);
  }, []);
  const scrollbarKey = `${isTableContainerMounted}-${ctx.viewMode}-${ctx.dataGridKey}`;

  useDataGridScrollbars(tableContainerRef, scrollbarKey);
  useDataGridVerticalScrollbar(
    tableContainerRef,
    scrollbarKey,
    ctx.viewMode !== 'list' && !ctx.hideVerticalScrollbar,
  );
  const gridColumns = useSelectionCheckboxColumns();

  const listViewSortModel = ctx.sortingMode === 'server' ? undefined : ctx.sortModel;
  const listViewSortColumns = ctx.sortingMode === 'server' ? undefined : ctx.processedColumns;

  const dataGridSlots = useMemo(
    () => ({
      toolbar: ctx.ToolbarComponent,
      noRowsOverlay: ctx.NoRowsOverlay,
      baseCheckbox: ctx.radioSelection ? ctx.RadioSlot : ctx.CheckboxSlot,
      baseBadge: SortPriorityBadge,
      columnMenu: GeneratikTableColumnMenu,
      ...CONTEXT_MENU_ICON_SLOTS,
      treeDataExpandIcon: KeyboardArrowDownIcon,
      treeDataCollapseIcon: KeyboardArrowUpIcon,
      detailPanelExpandIcon: DetailPanelExpandIcon,
      detailPanelCollapseIcon: DetailPanelCollapseIcon,
    }),
    [ctx.CheckboxSlot, ctx.NoRowsOverlay, ctx.RadioSlot, ctx.ToolbarComponent, ctx.radioSelection],
  );

  const dataGridSlotProps = useMemo(
    () => ({
      toolbar: {
        showQuickFilter: true,
        quickFilterProps: { debounceMs: 300 },
      },
    }),
    [],
  );

  const dataGridLocaleText = useMemo(
    () => ({
      toolbarFilters: t('filter'),
      toolbarColumns: t('Columns'),
      toolbarExport: t('buttons.export'),
      noRowsLabel: t('No data available'),
      MuiTablePagination: {
        labelRowsPerPage: t('Rows per page'),
      },
    }),
    [t],
  );

  const dataGridSx = useMemo(
    () =>
      buildDataGridSx({
        fillHeight: ctx.fillHeight,
        height: ctx.height,
        radioSelection: ctx.radioSelection,
        hasRowGrouping: Boolean(ctx.groupByField),
        consumerSx: ctx.sx,
      }),
    [ctx.fillHeight, ctx.height, ctx.radioSelection, ctx.sx, ctx.groupByField],
  );

  const rowSelectionPropagation = useMemo(() => ({ descendants: false, parents: false }), []);

  const getRowHeight = useCallback(
    (params: GridRowHeightParams) => {
      const rowNode = ctx.apiRef.current?.getRowNode(params.id);
      const hasChildren = (rowNode?.children?.length ?? 0) > 0;
      const depth = rowNode?.depth ?? 0;
      if (rowNode?.type === 'group' && ctx.groupByField && depth === 0 && hasChildren) {
        return 45;
      }
      return null;
    },
    [ctx.apiRef, ctx.groupByField],
  );

  const getDetailPanelContent = useMemo(
    () =>
      ctx.renderDetailPanel
        ? (params: GridRowParams) => {
            if (ctx.getRowCanExpand && !ctx.getRowCanExpand(params)) {
              return undefined;
            }
            return ctx.renderDetailPanel!(params);
          }
        : undefined,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ctx.getRowCanExpand, ctx.renderDetailPanel],
  );

  const onCellClick = useCallback(
    (params: GridCellParams, event: MuiEvent) => {
      if (params.field === GRID_CHECKBOX_SELECTION_FIELD) {
        const rowNode = params.rowNode as { isAutoGenerated?: boolean } | undefined;
        const isSyntheticGroup = !!rowNode?.isAutoGenerated;
        if (isSyntheticGroup) {
          return;
        }
        if (ctx.treeDataEnabled || ctx.radioSelection) {
          event.defaultMuiPrevented = true;
          ctx.toggleRowSelection(params.id);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [ctx.treeDataEnabled, ctx.radioSelection, ctx.toggleRowSelection],
  );

  const getDetailPanelHeight = useMemo(
    () =>
      ctx.renderDetailPanel
        ? typeof ctx.detailPanelHeight === 'function'
          ? ctx.detailPanelHeight
          : () => ctx.detailPanelHeight as number | 'auto'
        : undefined,
    [ctx.detailPanelHeight, ctx.renderDetailPanel],
  );

  return (
    <Dialog
      open={true}
      container={document.getElementById('page-root')}
      keepMounted
      fullWidth
      sx={getDialogSx(ctx.drawerOpen)}
      data-testid={ctx.dialogTestId ?? 'table-dialog'}
    >
      <DialogContent sx={dialogContentSx}>
        <TableContainer ref={setTableContainer} sx={tableContainerSx} data-testid="table-component">
          {ctx.showCustomToolbar && !ctx.hideHeader && ctx.title && (
            <GeneratikTableHeader
              title={ctx.title}
              titleIcon={ctx.titleIcon}
              filteredCount={ctx.totalCount}
              totalCount={ctx.totalCount}
              filters={ctx.activeFilters}
              onFilterRemove={ctx.onFilterRemove}
              onFilterClick={ctx.handleFilterClick}
              isModal
            />
          )}

          {ctx.showCustomToolbar && (
            <GeneratikTableToolbar
              startContent={ctx.toolbarStartContent}
              leftButtons={ctx.leftButtons}
              rightButtons={ctx.rightButtons}
              showSearch={ctx.showSearch}
              searchValue={ctx.searchValue}
              onSearchChange={ctx.onSearchChange}
              searchPlaceholder={ctx.searchPlaceholder}
              showColumns={ctx.showColumns}
              disableColumnsButton={ctx.disableColumnsButton}
              columns={ctx.processedColumns}
              visibleColumns={ctx.columnVisibilityModel}
              campaignColumnVisibility={ctx.campaignColumnVisibility}
              onColumnVisibilityChange={ctx.onColumnVisibilityChange}
              showSort={!!ctx.onSortModelChange}
              sortOptions={ctx.sortOptions}
              sortActions={ctx.sortActions}
              disableSortButton={ctx.disableSortButton}
              showGroup={!!ctx.onGroupChange}
              groupOptions={ctx.groupOptions}
              currentGroup={ctx.groupByField ?? undefined}
              onGroupChange={ctx.onGroupChange}
              showFilter={
                (ctx.filterFields?.length ?? 0) > 0 || (ctx.quickFilters?.length ?? 0) > 0
              }
              onFilterClick={ctx.handleFilterClick}
              filterButtonRef={ctx.filterButtonRef}
              showViewToggle={!!ctx.renderListViewRow}
              viewMode={ctx.viewMode}
              onViewModeChange={ctx.onViewModeChange}
              isModal
              disableGroupButton={ctx.disableGroupButton}
              showVariantToggle={ctx.hasTimelineColumns}
              variant={ctx.variant}
              onVariantChange={ctx.onVariantChange}
              timelineGranularity={ctx.timelineGranularity}
              onTimelineGranularityChange={ctx.onTimelineGranularityChange}
              effectiveTimelineAxis={ctx.effectiveTimelineAxis}
            />
          )}

          {ctx.predefinedFilters && (
            <Box sx={predefinedFiltersWrapperSx}>{ctx.predefinedFilters}</Box>
          )}

          {ctx.viewMode === 'list' && ctx.renderListViewRow ? (
            <GeneratikTableListView
              rows={ctx.rows}
              renderListViewRow={ctx.renderListViewRow}
              openFilters={ctx.handleFilterClick}
              getRowId={ctx.getRowId || ctx.defaultGetRowId}
              emptyStateMessage={ctx.emptyStateMessage}
              loading={ctx.loading}
              selectionModel={ctx.selectionModel}
              onToggleRowSelection={ctx.toggleRowSelection}
              groupByField={ctx.groupByField}
              groupOptions={ctx.groupOptions}
              getGroupItemPrice={ctx.getGroupItemPrice}
              priceForPeriod={ctx.groupPriceForPeriod}
              currency={ctx.currency}
              isRowSelectable={ctx.isRowSelectable}
              isChildRow={ctx.isChildRow}
              sortModel={listViewSortModel}
              sortColumns={listViewSortColumns}
            />
          ) : shouldShowHeadersLoadingPlaceholder(ctx) ? (
            <GeneratikTableHeadersLoadingPlaceholder />
          ) : (
            <StyledGeneratikDataGrid
              key={ctx.dataGridKey}
              ref={ctx.gridRootRef}
              autoHeight={ctx.height === 'auto'}
              apiRef={ctx.apiRef}
              rows={ctx.rows}
              columns={gridColumns}
              rowCount={ctx.rowCount ?? ctx.rows.length}
              getRowId={ctx.getRowId || ctx.defaultGetRowId}
              getRowClassName={ctx.getRowClassName}
              getCellClassName={ctx.getCellClassName}
              loading={ctx.loading}
              disableRowSelectionOnClick={ctx.disableRowSelectionOnClick}
              rowSelectionModel={ctx.selectionModel}
              onRowSelectionModelChange={ctx.handleRowSelectionChange}
              keepNonExistentRowsSelected
              columnVisibilityModel={ctx.effectiveColumnVisibilityModel}
              onColumnVisibilityModelChange={ctx.onColumnVisibilityModelChange}
              pagination
              paginationMode={ctx.paginationMode}
              paginationModel={ctx.paginationModel}
              onPaginationModelChange={ctx.onPaginationModelChange}
              pageSizeOptions={ctx.pageSizeOptions}
              hideFooter
              sortingMode={ctx.sortingMode}
              sortModel={ctx.sortModel}
              onSortModelChange={ctx.disableSortButton ? undefined : ctx.onSortModelChange}
              onColumnHeaderClick={ctx.disableSortButton ? undefined : ctx.onColumnHeaderClick}
              filterMode={ctx.filterMode}
              filterModel={ctx.filterModel}
              onFilterModelChange={ctx.onFilterModelChange}
              filterDebounceMs={300}
              treeData={ctx.treeDataEnabled}
              getTreeDataPath={
                ctx.treeDataEnabled
                  ? (ctx.effectiveGetTreeDataPath as
                      | ((row: (typeof ctx.rows)[number]) => string[])
                      | undefined)
                  : undefined
              }
              groupingColDef={ctx.groupingColDef}
              defaultGroupingExpansionDepth={ctx.defaultGroupingExpansionDepth ?? -1}
              rowSelectionPropagation={rowSelectionPropagation}
              onRowClick={ctx.handleRowClick}
              onRowDoubleClick={ctx.handleRowDoubleClick}
              onCellClick={onCellClick}
              disableColumnMenu={ctx.hideColumnMenu}
              disableColumnReorder={!ctx.columnReordering}
              pinnedColumns={ctx.pinnedColumns}
              onPinnedColumnsChange={ctx.onPinnedColumnsChange}
              onColumnOrderChange={ctx.handleColumnOrderChange}
              onColumnWidthChange={ctx.handleColumnWidthChange}
              density={ctx.density}
              rowHeight={55}
              getRowHeight={getRowHeight}
              columnHeaderHeight={45}
              disableAggregation
              checkboxSelection={ctx.checkboxSelection || ctx.radioSelection}
              isRowSelectable={ctx.isRowSelectable}
              getDetailPanelContent={getDetailPanelContent}
              getDetailPanelHeight={getDetailPanelHeight}
              detailPanelExpandedRowIds={ctx.detailPanelExpandedRowIds}
              onDetailPanelExpandedRowIdsChange={ctx.onDetailPanelExpandedRowIdsChange}
              slots={dataGridSlots}
              slotProps={dataGridSlotProps}
              localeText={dataGridLocaleText}
              sx={dataGridSx}
              rowBufferPx={ROW_OVERSCAN_PX}
              columnBufferPx={DEFAULT_COLUMN_BUFFER_PX}
            />
          )}

          {((ctx.filterFields?.length ?? 0) > 0 ||
            (ctx.quickFilters && ctx.quickFilters.length > 0)) && (
            <GeneratikTableFilterPanel
              anchorEl={ctx.filterAnchorEl}
              open={Boolean(ctx.filterAnchorEl)}
              onClose={ctx.handleFilterClose}
              fields={ctx.filterFields}
              values={ctx.filterValues}
              onChange={ctx.onFilterValuesChange}
              onApply={ctx.handleFilterApplyInternal}
              onReset={ctx.onFilterReset}
              predefinedFilters={ctx.quickFilters}
              campaignColumnVisibility={ctx.campaignColumnVisibility}
              initialFocusFieldId={ctx.focusFieldId}
            />
          )}
        </TableContainer>
      </DialogContent>

      <DialogActions sx={dialogActionsSx}>
        <GeneratikTableFooter
          hidePagination={ctx.hidePagination}
          page={ctx.paginationModel.page}
          pageSize={ctx.paginationModel.pageSize}
          rowCount={ctx.rowCount ?? ctx.rows.length}
          pageSizeOptions={ctx.pageSizeOptions}
          onPageChange={ctx.handlePageChange}
          onPageSizeChange={ctx.handlePageSizeChange}
          pageTotal={ctx.pageTotal}
          pageSelected={ctx.pageSelected}
          spacesTotal={ctx.spacesTotal}
          productionTotal={ctx.productionTotal}
          grandTotal={ctx.grandTotal}
          selectedRowsCount={ctx.selectedRows.length}
          currency={ctx.currency}
          modalActions={ctx.modalActions}
          stackedModalFooter={ctx.stackedModalFooter}
        />
      </DialogActions>
    </Dialog>
  );
};

export default GeneratikTableDialogDisplay;
